import { Component, Input, Output, EventEmitter } from '@angular/core'
import { CommonModule } from '@angular/common'
import { TooltipModule } from 'primeng/tooltip'
import {
  TourStepView,
  AddressCopyPayload,
  AlternativeSlotPayload,
  DayShiftMove,
} from '../../affair-pool-optimization.types'
import {
  formatTime,
  formatDuration,
  formatDistance,
  formatDate,
} from '../../pool-optimization-formatters'

@Component({
  selector: 'app-pool-day-timeline',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  templateUrl: './pool-day-timeline.component.html',
  styleUrl: './pool-day-timeline.component.scss',
})
export class PoolDayTimelineComponent {
  @Input() selectedTourSteps: TourStepView[] = []
  @Input() startStep: TourStepView | null = null
  @Input() endStep: TourStepView | null = null
  @Input() selectedTechnicianHomeAddress = ''
  @Input() highlightedAffairId: number | null = null
  @Input() copiedAddressAffairId: number | null = null
  @Input() optimizingAffairIds = new Set<number>()
  @Input() recalculating = false
  /** La proposition implique un changement de jour d'interventions programmées. */
  @Input() requiresDayShift = false
  /** Détail des déplacements inter-jours (y compris sur d'autres jours que la tournée). */
  @Input() dayShiftMoves: DayShiftMove[] = []

  /** Détail des changements de jour déplié (repliable pour gagner de la place). */
  dayShiftExpanded = true

  toggleDayShiftExpanded(): void {
    this.dayShiftExpanded = !this.dayShiftExpanded
  }

  @Output() timelineStepClick = new EventEmitter<number>()
  @Output() addressCopied = new EventEmitter<AddressCopyPayload>()
  @Output() alternativeSlotNavigated = new EventEmitter<AlternativeSlotPayload>()
  @Output() manualScheduleRequested = new EventEmitter<TourStepView>()
  @Output() manualDeleteRequested = new EventEmitter<TourStepView>()

  readonly formatTime = formatTime
  readonly formatDuration = formatDuration
  readonly formatDistance = formatDistance
  readonly formatDate = formatDate

  /** Masque ponctuellement les interventions déjà programmées (mode focus). */
  hideScheduled = false

  /** Nombre d'affaires du vivier à programmer dans la tournée. */
  get poolCount(): number {
    return this.selectedTourSteps.filter((s) => s.type === 'pool_affair').length
  }

  /** Nombre d'interventions déjà programmées dans la tournée. */
  get scheduledCount(): number {
    return this.selectedTourSteps.filter(
      (s) => s.type === 'scheduled_intervention'
    ).length
  }

  /** Nombre d'interventions programmées à déplacer (action requise). */
  get movedCount(): number {
    return this.selectedTourSteps.filter(
      (s) => s.type === 'scheduled_intervention' && s.moved
    ).length
  }

  /** Nombre d'interventions à déplacer sur un AUTRE jour dans cette tournée. */
  get movedDayCount(): number {
    return this.selectedTourSteps.filter(
      (s) => s.type === 'scheduled_intervention' && s.movedDay
    ).length
  }

  /** Libellé de la période de décalage autorisée par EROS. */
  postponableLabel(period: TourStepView['postponablePeriod']): string {
    switch (period) {
      case 'day':
        return 'Décalable dans la journée'
      case 'week':
        return 'Décalable dans la semaine'
      case 'month':
        return 'Décalable dans le mois'
      default:
        return ''
    }
  }

  /**
   * Fenêtre de planification souhaitée (« 12 → 26 juil. ») si transmise par
   * EROS, sinon null. Mutuellement exclusive avec l'échéance : on affiche la
   * fenêtre souhaitée quand elle existe, sinon l'échéance.
   */
  planningWindowLabel(step: TourStepView): string | null {
    const start = step.wishedPlanningStartDate
    const end = step.wishedPlanningEndDate
    if (start && end) return `${formatDate(start)} → ${formatDate(end)}`
    if (start) return `dès ${formatDate(start)}`
    if (end) return `jusqu'au ${formatDate(end)}`
    return null
  }

  /**
   * Plage horaire imposée par l'ERP (« 08:00 – 12:00 ») pour une intervention
   * programmée, sinon null. Les bornes sont déjà au format « HH:MM ».
   */
  timeSlotLabel(step: TourStepView): string | null {
    const start = step.timeSlotStart
    const end = step.timeSlotEnd
    if (start && end) return `${start} – ${end}`
    if (start) return `dès ${start}`
    if (end) return `jusqu'à ${end}`
    return null
  }

  toggleHideScheduled(): void {
    this.hideScheduled = !this.hideScheduled
  }

  /** Id de la première affaire à programmer de la tournée. */
  get firstPoolAffairId(): number | null {
    const first = this.selectedTourSteps.find((s) => s.type === 'pool_affair')
    return first?.affairId ?? null
  }

  /**
   * Carte dépliée (actions, motif d'appel, autres dates) : la carte active
   * (cliquée) et, toujours, la première affaire à programmer.
   */
  isCardExpanded(step: TourStepView): boolean {
    return (
      step.isCurrentAffair ||
      (step.affairId != null && step.affairId === this.firstPoolAffairId)
    )
  }

  onStepClick(step: TourStepView, event: MouseEvent): void {
    const target = event.target as HTMLElement | null
    if (target?.closest('a,button')) return
    if (step.type !== 'pool_affair' || !step.affairId) return
    this.timelineStepClick.emit(step.affairId)
  }

  onCopyAddress(step: TourStepView, event: Event): void {
    if (step.affairId == null) return
    this.addressCopied.emit({
      affair: {
        affairId: step.affairId,
        postalCode: step.postalCode ?? null,
        locality: step.locality ?? null,
      },
      event,
    })
  }

  isAffairOptimizing(affairId: number | undefined): boolean {
    return affairId != null && this.optimizingAffairIds.has(affairId)
  }
}
